import React from "react";
import PropTypes from "prop-types";
import { HelpCircle } from "lucide-react";
import { meterpointService } from "../../../../services/meterpoint";
import styles from "./AddressSelection.module.css";
/* global globalThis */

const getGlobalObject = () => {
  if (typeof globalThis !== "undefined") return globalThis;
  return {};
};

const reportError = (err) => {
  const sentry = getGlobalObject().Sentry;
  if (sentry?.captureException) {
    sentry.captureException(err);
  }
};

const MeterNumberInput = ({
  selectedFuel,
  meterNumber,
  onMeterNumberChange,
  showMeterHelp,
  setShowMeterHelp,
  meterNumberError,
  setMeterNumberError,
  meterValidated,
  setMeterValidated,
  onManualAddressChange,
}) => {
  const getMeterNumberAriaDescribedBy = () => {
    if (showMeterHelp) {
      return "meter-number-help";
    }
    if (meterNumberError) {
      return "meter-number-error";
    }
    return undefined;
  };

  const getMeterDigitAriaDescribedBy = (isFirstSegment) => {
    if (!isFirstSegment) {
      return undefined;
    }
    return getMeterNumberAriaDescribedBy();
  };

  const handleValidate = async () => {
    const segments = meterNumber ? meterNumber.split("-") : [];
    const totalDigits = segments.reduce(
      (sum, segment) => sum + segment.length,
      0,
    );

    if (!meterNumber || totalDigits < 21) {
      setMeterNumberError("Please enter your meter number.");
      return;
    }

    try {
      const mpan = meterNumber.replaceAll("-", "");
      const res = await meterpointService.validateMpan(mpan.substring(0, 8));

      if (res.mpanType === null) {
        setMeterNumberError(
          "Meter details could not be found. Please check and try again",
        );
      } else {
        const meterType = res?.mpanType || "";
        onManualAddressChange("detectedMeterType", meterType);
        onManualAddressChange("mpanCore", mpan);
        setMeterNumberError("");
        setMeterValidated(true);
      }
    } catch (error) {
      reportError(error);
      setMeterNumberError(
        "Meter details could not be found. Please check and try again",
      );
    }
  };

  const handleClear = () => {
    setMeterValidated(false);
    onManualAddressChange("meterNumber", "");
    setMeterNumberError("");
  };

  const renderGasMeterInput = () => (
    <div className={styles.inputWithIcon}>
      <input
        id="meter-number"
        type="text"
        maxLength="11"
        value={meterNumber || ""}
        onChange={(e) => {
          const value = e.target.value.replaceAll(/\D/g, "");
          onMeterNumberChange(value);
        }}
        className={styles.formInput}
        aria-describedby={getMeterNumberAriaDescribedBy()}
        aria-invalid={!!meterNumberError}
      />
    </div>
  );

  const renderElectricityMeterInput = () => (
    <>
      <div className={styles.meterNumberInputs}>
        <span className={styles.meterPrefix}>S</span>
        <div className={styles.meterNumberGrid}>
          {Array.from({ length: 7 }, (_, i) => {
            const getMaxLength = (index) => {
              if (index === 0 || index === 3) return "2";
              if (index === 1 || index === 2 || index === 6) return "3";
              if (index === 4 || index === 5) return "4";
            };

            const getCurrentSegmentValue = () => {
              if (!meterNumber) return "";
              const segments = meterNumber.split("-");
              return segments[i] || "";
            };

            return (
              <input
                key={`meter-segment-${i}`}
                type="text"
                id={i === 0 ? "meter-number" : undefined}
                maxLength={getMaxLength(i)}
                value={getCurrentSegmentValue()}
                className={styles.meterDigitInput}
                aria-label={`Meter number segment ${i + 1} of 7`}
                aria-describedby={getMeterDigitAriaDescribedBy(i === 0)}
                aria-invalid={!!meterNumberError}
                onChange={(e) => {
                  const value = e.target.value;
                  const segments = meterNumber
                    ? meterNumber.split("-")
                    : new Array(7).fill("");
                  segments[i] = value;
                  const newMeterNumber = segments.join("-");
                  onMeterNumberChange(newMeterNumber);

                  if (
                    value.length === Number.parseInt(getMaxLength(i)) &&
                    i < 6
                  ) {
                    const nextInput = e.target.parentElement.children[i + 1];
                    if (nextInput) nextInput.focus();
                  }
                }}
              />
            );
          })}
        </div>
      </div>
      {meterValidated ? (
        <div className={styles.validatedContainer}>
          <div className="flex gap-3">
            <div className="mt-[6px]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24px"
                viewBox="0 -960 960 960"
                width="24px"
                fill="#0b74c0"
              >
                <path d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" />
              </svg>
            </div>
            <p className={styles.validatedText}>Validated</p>
          </div>
          <button onClick={handleClear} className={styles.clearLink}>
            Click here to clear the meter number
            <br />
            and try again
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={styles.validateButton}
          onClick={handleValidate}
        >
          VALIDATE
        </button>
      )}
    </>
  );

  return (
    <div className={styles.formGroup}>
      <div className={styles.labelSection}>
        <label htmlFor="meter-number" className={styles.formLabel}>
          {selectedFuel === "gas"
            ? "Your gas Meter Point Reference Number (MPRN)"
            : "Meter number"}
          <button
            type="button"
            className={styles.helpIcon}
            onClick={() => setShowMeterHelp(!showMeterHelp)}
            aria-label={`${showMeterHelp ? "Hide" : "Show"} meter number help information`}
            aria-expanded={showMeterHelp}
            aria-controls="meter-number-help"
          >
            <HelpCircle size={20} />
          </button>
        </label>
        {showMeterHelp && (
          <section
            id="meter-number-help"
            className={styles.infoBox}
            aria-label="Meter number help information"
          >
            {selectedFuel === "gas"
              ? "Also referred to as an MPR, this is the gas meter number associated with your business site address and can be found on your bill or statement."
              : "This is the electricity meter number associated with your business site address and can be found on your bill or statement."}
          </section>
        )}
      </div>

      <div className={styles.meterNumberContainer}>
        {selectedFuel === "gas"
          ? renderGasMeterInput()
          : renderElectricityMeterInput()}
      </div>
      {meterNumberError && (
        <div
          id="meter-number-error"
          className={styles.errorMessage}
          role="alert"
          aria-live="polite"
        >
          {meterNumberError}
        </div>
      )}
    </div>
  );
};

MeterNumberInput.propTypes = {
  selectedFuel: PropTypes.string.isRequired,
  meterNumber: PropTypes.string,
  onMeterNumberChange: PropTypes.func.isRequired,
  showMeterHelp: PropTypes.bool.isRequired,
  setShowMeterHelp: PropTypes.func.isRequired,
  meterNumberError: PropTypes.string.isRequired,
  setMeterNumberError: PropTypes.func.isRequired,
  meterValidated: PropTypes.bool.isRequired,
  setMeterValidated: PropTypes.func.isRequired,
  onManualAddressChange: PropTypes.func.isRequired,
};

export default MeterNumberInput;
